/**
 * CatalogReviewSummary — the headline numbers of the review workspace and the one next step worth taking. Demo page:
 * `/` (overview stats above an alert). Tiles are coloured only when the number needs action.
 */
import { AlertTriangle } from 'lucide-react';
import { Button, Callout, StatStrip, type CalloutTone } from '../../../components/ui/index.js';
import type {
  CatalogCandidateListItem,
  CatalogItemVersion,
  CatalogSource,
  DaemonReviewQueueItem,
} from './catalog-types.js';
import { summarizeCatalogReviewAudit } from './catalog-helpers.js';
import type { CatalogTabId } from './useCatalogReview.js';

interface NextStep {
  tone: CalloutTone;
  title: string;
  body: string;
  tab: CatalogTabId;
  action: string;
  mode: 'navigate' | 'retry' | 'process' | 'failedCandidates';
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

function nextStepFor(audit: ReturnType<typeof summarizeCatalogReviewAudit>): NextStep {
  if (audit.blockedReviews > 0) {
    return {
      tone: 'danger',
      title: `${plural(audit.blockedReviews, 'review job', 'review jobs')} blocked`,
      body: 'The next action is to enable or repair the review daemon, then retry the blocked reviews.',
      tab: 'queue',
      action: 'Resolve blockers',
      mode: 'navigate',
    };
  }
  if (audit.failedReviews > 0) {
    return {
      tone: 'danger',
      title: `${plural(audit.failedReviews, 'review job', 'review jobs')} failed`,
      body: 'Retry failed reviews after fixing the underlying scanner, policy, or candidate data issue.',
      tab: 'queue',
      action: 'Retry reviews',
      mode: 'retry',
    };
  }
  if (audit.failedCandidates > 0) {
    return {
      tone: 'danger',
      title: `${plural(audit.failedCandidates, 'candidate', 'candidates')} failed daemon review`,
      body: 'One or more supervising daemons produced findings. Open a candidate to review the actual finding text and recommendation.',
      tab: 'candidates',
      action: 'Review findings',
      mode: 'failedCandidates',
    };
  }
  if (audit.readyCandidates > 0) {
    return {
      tone: 'success',
      title: `${plural(audit.readyCandidates, 'candidate', 'candidates')} ready for human review`,
      body: 'Review provenance and payloads, then promote, defer, or reject each candidate.',
      tab: 'candidates',
      action: 'Review candidates',
      mode: 'navigate',
    };
  }
  if (audit.pendingCandidates > 0) {
    return {
      tone: 'warning',
      title: `${plural(audit.pendingCandidates, 'candidate', 'candidates')} waiting on daemon checks`,
      body: 'Assigned review daemons must clear candidates before they become promotable.',
      tab: 'queue',
      action: 'Run reviews now',
      mode: 'process',
    };
  }
  return {
    tone: 'accent',
    title: 'No catalog actions pending',
    body: 'Run a discovery daemon to collect source-backed candidates.',
    tab: 'candidates',
    action: 'View candidates',
    mode: 'navigate',
  };
}

interface CatalogReviewSummaryProps {
  candidates: CatalogCandidateListItem[];
  sources: CatalogSource[];
  queueItems: DaemonReviewQueueItem[];
  versions: CatalogItemVersion[];
  onTabChange: (tab: CatalogTabId) => void;
  onRetryReviews: () => void;
  onProcessReviews: () => void;
  onShowFailedCandidates: () => void;
  busy: boolean;
}

export function CatalogReviewSummary({
  candidates,
  sources,
  queueItems,
  versions,
  onTabChange,
  onRetryReviews,
  onProcessReviews,
  onShowFailedCandidates,
  busy,
}: CatalogReviewSummaryProps) {
  const audit = summarizeCatalogReviewAudit({ candidates, sources, queueItems, versions });
  const step = nextStepFor(audit);
  const latestQueuedAt = candidates
    .map((candidate) => candidate.queuedAt)
    .filter(Boolean)
    .sort()
    .at(-1);

  const run = () => {
    if (step.mode === 'retry') onRetryReviews();
    else if (step.mode === 'process') onProcessReviews();
    else if (step.mode === 'failedCandidates') onShowFailedCandidates();
    else onTabChange(step.tab);
  };

  return (
    <div>
      <StatStrip
        className="mb-5"
        cells={[
          { label: 'Candidates', value: candidates.length },
          { label: 'Sources', value: sources.length, sub: `${audit.sourceFragments} fragments` },
          {
            label: 'Integrity blocked',
            value: audit.integrityBlocked,
            severity: audit.integrityBlocked > 0 ? 'danger' : undefined,
          },
          {
            label: 'Failed candidates',
            value: audit.failedCandidates,
            severity: audit.failedCandidates > 0 ? 'danger' : undefined,
          },
          {
            label: 'Ready for review',
            value: audit.readyCandidates,
            severity: audit.readyCandidates > 0 ? 'success' : undefined,
          },
          { label: 'Versions', value: versions.length },
        ]}
      />
      <Callout
        tone={step.tone}
        icon={step.tone === 'danger' ? AlertTriangle : undefined}
        title={step.title}
        actions={
          <Button
            size="sm"
            variant={step.tone === 'danger' ? 'danger' : 'secondary'}
            loading={busy && step.mode !== 'navigate'}
            onClick={run}
          >
            {step.action}
          </Button>
        }
      >
        {step.body}
        {latestQueuedAt && (
          <span className="mt-1 block text-xs opacity-80">
            Latest discovery: {new Date(latestQueuedAt).toLocaleString()}
          </span>
        )}
      </Callout>
    </div>
  );
}
